import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { BsTable } from "react-icons/bs";
import Select from "react-select";
import { PDFExport } from "@progress/kendo-react-pdf";

// Services
import {
  getUnitHolderStatement,
  getUnitHolderStatementUpdated,
} from "../../stores/services/report.service";
import {
  getDailyNavOfAllFunds,
  getFundByAmc,
} from "../../stores/services/funds.service";

// Components
import ReportHeader from "./report-header";
import DividendCard from "../dividend/AnnouncementListing";
import ReportTemplates from "../../components/ReportTemplate/ReportTemplate";
import {
  ParticipantStatementTable,
  RegularStatementTable,
  UnitHolderSearchForm,
} from "./unitholder-statement/components";

// Hooks & Utils
import { useUnitHolderSearch } from "../../hooks/useSearchUnitholder";
import { processParticipantData } from "./unitholder-statement/utils";
import { isNumber, numberWithCommas } from "../../utils/customFunction";
import { COLUMN_HEADINGS } from "./unitholder-statement/constants";
import { useSelector } from "react-redux";
import { useCustomStylesDropDown } from "../../assets/css/customStyle";

import html2pdf from "html2pdf.js";
import { exportToTxt, generateExcel } from "../../utils/customFunction";

// ── Inline number formatter ──────────────────────────────────────────────────
const FlatNumber: React.FC<{ value: string | number }> = ({ value }) => (
  <span
    style={{
      fontVariantNumeric: "tabular-nums",
      verticalAlign: "baseline",
      display: "inline",
      lineHeight: "inherit",
      fontSize: "inherit",
    }}
  >
    {value}
  </span>
);

const UnitHolderStatement = () => {
  const contentRef = useRef(null);
  const pdfExportComponent = useRef<PDFExport>(null);

  // Session data
  const email = sessionStorage.getItem("email") || "";
  const amc_code = sessionStorage.getItem("amc_code") || "";
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);
  const txnTypes = useSelector((state: any) => state.txnTypes);

  // Form state
  const [fund, setFund] = useState("all");
  const [folioNo, setFolioNo] = useState("");
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  // Data state
  const [unitHolderStatement, setUnitHolderStatement] = useState<any[]>([]);
  const [unitHolderData, setUnitHolderData] = useState<any>([]);
  const [allFunds, setAllFunds] = useState<any[]>([]);
  const [dailyNav, setDailyNav] = useState<any[]>([]);
  const [participantRows, setParticipantRows] = useState<any[]>([]);

  // Response state
  const [resFolioNo, setResFolioNo] = useState("");
  const [resFromDate, setResFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [resToDate, setResToDate] = useState(moment().format("YYYY-MM-DD"));

  // Loading states
  const [loading, setLoading] = useState(false);
  const [folioLoading, setFolioLoading] = useState(false);

  // Validation errors
  const [folioNoErr, setFolioNoErr] = useState("");
  const [fromDateErr, setFromDateErr] = useState("");
  const [toDateErr, setToDateErr] = useState("");

  // Report customization
  const [selectedValues, setSelectedValues] = useState<any>({
    heading: "",
    sub_heading: "",
    columns: COLUMN_HEADINGS?.map?.((col) => col?.value),
    balance: ["Opening Balance", "Closing Balance"],
  });
  const [fontSizeVal, setFontSizeVal] = useState<any>(18);
  const [paperSizeVal, setPaperSizeVal] = useState("A4");
  const [layoutVal, setLayoutVal] = useState("Landscape");
  const [marginPDF, setMarginPDF] = useState("1cm");
  const [scalePDF, setScalePDF] = useState(0.5);
  const [itemsPerPage, setItemsPerPage] = useState<any>(10);
  const [itemsToShow, setItemsToShow] = useState(10);
  const { customStyles } = useCustomStylesDropDown();
  const [screenSizePerPdf, setScreenSizePerPdf] = useState();
  const [dimensionOfPages, setDimensionOfPages] = useState<any>();

  const {
    unitholderOptions,
    unitholderLoading,
    fetchInitialUnitholders,
    searchUnitholders,
  } = useUnitHolderSearch(email);

  const [unitholderSearchInput, setUnitholderSearchInput] = useState("");
  const isParticipant = unitHolderData?.unitholder_Type === "Participant";

  useEffect(() => {
    const getAllFunds = async () => {
      setFolioLoading(true);
      try {
        const response = await getFundByAmc(email, amc_code, "");
        setAllFunds(response.data.data);
      } catch (err: any) {
        toast.error(err.response?.data?.message || "Failed to load funds");
      } finally {
        setFolioLoading(false);
      }
    };
    fetchInitialUnitholders();
    getAllFunds();
  }, []);

  useEffect(() => {
    if (!unitholderSearchInput) return;
    const timeoutId = setTimeout(() => {
      searchUnitholders(unitholderSearchInput);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [unitholderSearchInput]);

  const CapTxnType = (type: any) => {
    const found = txnTypes.find((t: any) => t.api_name === type);
    return found ? found.dis_name : type;
  };

  const searchData = async () => {
    setItemsPerPage(10);
    try {
      setUnitHolderStatement([]);
      setLoading(true);
      if (!folioNo) {
        setFolioNoErr("required-border");
        setLoading(false);
        return;
      }
      if (!toDate) {
        setToDateErr("required-border");
        setLoading(false);
        return;
      }
      if (!fromDate) {
        setFromDateErr("required-border");
        setLoading(false);
        return;
      }

      const navResponse = await getDailyNavOfAllFunds(email, amc_code, toDate);
      if (navResponse.status === 200) {
        setDailyNav(Object.values(navResponse.data));
      }

      const testResponse = await getUnitHolderStatementUpdated(
        email,
        fund !== "" && fund !== "all" ? fund : "",
        amc_code,
        folioNo,
        fromDate,
        toDate,
      );

      if (testResponse?.status === 200 && testResponse?.data?.status === 200) {
        setResFolioNo(folioNo);
        setResToDate(toDate);
        setResFromDate(fromDate);

        const unitSoldArr = [
          "cgtrefund",
          "refundofcapital",
          "dividendreinvestment",
          "dividend",
          "bonus",
          "transferlienin",
          "transferin",
          "conversionin",
          "saleofunit",
        ];
        const redeemedArr = [
          "zakatondividend",
          "zakatonbonusencashment",
          "dividendpaid",
          "taxondividend",
          "taxonbonusencashment",
          "bonusencashment",
          "transferlienout",
          "transferout",
          "conversionout",
          "redemptionofunits",
        ];

        const temp = testResponse?.data?.data?.balance_unit?.map((ele) => {
          let ClosingUnitsTest = 0,
            total_refundCapital = 0,
            total_units = 0;
          let total_grossAmount = 0,
            total_saleLoad = 0,
            total_redemptionLoad = 0;
          let total_CGT = 0,
            total_netAmount = 0,
            totalLoad = 0,
            totaltax = 0;
          let broker = "";

          const innerArr = ele?.forPeriodTxns?.map((ele1, index) => {
            const parsedTransfree =
              ele1?.transfrees && JSON.parse(ele1?.transfrees);
            if (unitSoldArr.some((e) => e === ele1?.txnr_type)) {
              ClosingUnitsTest +=
                index > 0 ? +ele1?.units : +ele1?.units + +ele.openingUnits;
            } else if (redeemedArr.some((e) => e === ele1?.txnr_type)) {
              ClosingUnitsTest -=
                index > 0 ? +ele1?.units : +ele1?.units - +ele.openingUnits;
            } else if (ele1?.txnr_type === "transferofunits") {
              const isReceiver =
                parsedTransfree?.filter((fol) => fol?.folio_no === folioNo)
                  ?.length > 0;
              broker = isReceiver ? "receiver" : "sender";
              if (isReceiver) {
                ClosingUnitsTest +=
                  index > 0 ? +ele1?.units : +ele1?.units - +ele.openingUnits;
              } else {
                ClosingUnitsTest -=
                  index > 0 ? +ele1?.units : +ele1?.units - +ele.openingUnits;
              }
            }
            total_refundCapital += ele1?.refund_of_capital || 0;
            total_units += ele1?.units || 0;
            total_grossAmount += ele1?.gross_amount || 0;
            total_saleLoad += ele1?.sale_load || 0;
            total_redemptionLoad += ele1?.redemption_load || 0;
            total_CGT += ele1?.tax_cgt || 0;
            total_netAmount += ele1?.net_amount || 0;
            totalLoad += (ele1?.sale_load || 0) + (ele1?.redemption_load || 0);
            totaltax += (ele1?.tax_cgt || 0) + (ele1?.tax_on_dvidend || 0);
            return {
              ...ele1,
              closingUnits: ClosingUnitsTest,
              broker,
              load: (ele1?.sale_load || 0) + (ele1?.redemption_load || 0),
              tax: (ele1?.tax_cgt || 0) + (ele1?.tax_on_dvidend || 0),
            };
          });

          return {
            ...ele,
            forPeriodTxns: innerArr,
            total_netAmount,
            total_CGT,
            total_redemptionLoad,
            total_saleLoad,
            total_grossAmount,
            total_units,
            total_refundCapital,
            total_load: totalLoad,
            total_tax: totaltax,
          };
        });

        setUnitHolderStatement(temp);
        toast.success("Unitholder Data Found");
      } else {
        toast.error("No record found!");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Request Failed!");
    } finally {
      setLoading(false);
    }
  };

  const searchDataParticipant = async () => {
    try {
      const fund_wise_opening_balance: Record<string, any> = {};
      setLoading(true);
      setUnitHolderStatement([]);
      if (!folioNo) {
        setFolioNoErr("required-border");
        setLoading(false);
        return;
      }

      const searchResponse = await getUnitHolderStatement(
        email,
        folioNo,
        fromDate,
        toDate,
        "participant",
      );

      if (
        searchResponse.data.status === 200 &&
        searchResponse.data.data.length > 0
      ) {
        setResFromDate(fromDate);
        setResToDate(toDate);

        const temp1 = searchResponse.data.data.map((item: any) => {
          const record = item.Record;
          return (
            record.systematic_investment?.investment_blocks?.[0]?.fund_code ||
            record.fund_code
          );
        });
        const unique_headings = Array.from(new Set(temp1));

        const allData = unique_headings.map((head: any) => {
          let count = 0,
            count_balance = 0,
            open_balance = 0,
            totalunits = 0;
          let totalgrossamount = 0,
            totalnetamount = 0,
            totalcgt = 0;
          let totalsaleload = 0,
            totalredemptionload = 0,
            totalrefundCapital = 0;

          const filteredData = searchResponse.data.data
            .filter((data: any) => {
              const record = data.Record;
              return record.systematic_investment?.investment_blocks?.[0]
                ?.fund_code
                ? record.systematic_investment.investment_blocks[0]
                    .fund_code === head
                : record.fund_code === head;
            })
            .map((data: any) => {
              const record = { ...data.Record };
              record.units = isNumber(record.units);
              record.sale_load = isNumber(record.sale_load);
              count += 1;
              if (count === 1) {
                open_balance = fund_wise_opening_balance[head]
                  ? parseFloat(
                      String(fund_wise_opening_balance[head]).replace(/,/g, ""),
                    ) || 0
                  : 0;
                count_balance += open_balance;
              }
              totalgrossamount +=
                parseFloat(
                  String(record.gross_amount || 0).replace(/,/g, ""),
                ) || 0;
              totalnetamount +=
                parseFloat(String(record.net_amount || 0).replace(/,/g, "")) ||
                0;
              totalcgt +=
                parseFloat(String(record.tax_cgt || 0).replace(/,/g, "")) || 0;
              totalredemptionload +=
                parseFloat(
                  String(record.redemption_load || 0).replace(/,/g, ""),
                ) || 0;
              totalsaleload +=
                parseFloat(String(record.sale_load || 0).replace(/,/g, "")) ||
                0;
              totalrefundCapital +=
                parseFloat(
                  String(record.refund_of_capital || 0).replace(/,/g, ""),
                ) || 0;
              const units =
                parseFloat(String(record.units || 0).replace(/,/g, "")) || 0;
              count_balance -= units;
              totalunits += units;
              record.count_balance = count_balance;
              return record;
            });

          return {
            heading: `${head}`,
            data1: filteredData.map((r: any) => ({ Record: r })),
            opening_balance: open_balance,
            closing_balance: count_balance,
            total_units: totalunits.toFixed(4),
            total_grossAmount: totalgrossamount.toFixed(4),
            total_netAmount: totalnetamount.toFixed(4),
            total_CGT: totalcgt.toFixed(4),
            total_redemptionLoad: totalredemptionload.toFixed(4),
            total_saleLoad: totalsaleload.toFixed(4),
            total_refundCapital: totalrefundCapital.toFixed(4),
          };
        });

        const UpdatedArr = processParticipantData(allData);
        setUnitHolderStatement(allData);
        setParticipantRows(UpdatedArr);
      } else {
        toast.error("Data not found.");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const date = new Date();
  const fixedOrder = [
    "Date",
    "TXN ID",
    "Description",
    "Fund Units",
    "NAV",
    "Gross Amount",
    "Load",
    "Tax",
    "Net Amount",
    "Closing Units",
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // buildExportRows — FIXED
  // Key fixes:
  //   1. Raw numeric fields are formatted ONCE here; the column-map no longer
  //      double-wraps them with numberWithCommas().
  //   2. Participant rows now map to the correct fields (date, txn_id, nav,
  //      units, amount, closing_balance) instead of repeating Fund_Units.
  //   3. The "Total" / "Closing" sentinel rows include proper numeric strings
  //      so they appear correctly in Excel.
  //   4. Header row for each fund group now only spans the first enabled column
  //      (unchanged logic) but the opening-balance note is always placed in the
  //      last column regardless of which columns are selected.
  // ─────────────────────────────────────────────────────────────────────────────
  const buildExportRows = () => {
    let openingBalanceIs: any = 0;
    let nav = "";
    let balance = "";

    // ── PARTICIPANT path ──────────────────────────────────────────────────────
    if (isParticipant) {
      const rows: any[] = [];

      participantRows.forEach((unitHolder) => {
        // Fund heading row
        rows.push({
          Date: `${unitHolder?.fund_code}`,
          "TXN ID": "",
          Description: "",
          "Fund Units": "",
          NAV: "",
          "Gross Amount": "",
          Load: "",
          Tax: "",
          "Net Amount": "",
          "Closing Units": selectedValues?.balance?.includes("Opening Balance")
            ? `Opening Balance: ${numberWithCommas(
                parseFloat(unitHolder?.opening_balance || 0).toFixed(4),
              )}`
            : "",
        });

        // Transaction rows
        unitHolder?.subFunds?.forEach((item: any) => {
          rows.push({
            Date: new Date(item?.date).toLocaleDateString("en-GB"),
            "TXN ID": item?.txn_id || "",
            Description:
              item?.txnr_type === "changeofallocation" && item?.allocation_type
                ? `${CapTxnType(item?.txnr_type)} (${item?.allocation_type})`
                : CapTxnType(item?.txnr_type) || "",
            // ✅ FIX: correct field names
            "Fund Units": numberWithCommas(
              parseFloat(item?.units || 0).toFixed(4),
            ),
            NAV: parseFloat(item?.nav || 0).toFixed(4),
            "Gross Amount": numberWithCommas(
              parseFloat(item?.amount || 0).toFixed(4),
            ),
            Load: "0.00",
            Tax: "0.00",
            "Net Amount": numberWithCommas(
              parseFloat(item?.amount || 0).toFixed(4),
            ),
            "Closing Units": numberWithCommas(
              parseFloat(item?.closing_balance || 0).toFixed(4),
            ),
          });
        });

        // Total row for this fund group
        rows.push({
          Date: "Total",
          "TXN ID": "",
          Description: "",
          "Fund Units": numberWithCommas(
            parseFloat(unitHolder?.total_units || 0).toFixed(4),
          ),
          NAV: "",
          "Gross Amount": numberWithCommas(
            parseFloat(unitHolder?.total_amount || 0).toFixed(4),
          ),
          Load: "",
          Tax: "",
          "Net Amount": "",
          "Closing Units": selectedValues?.balance?.includes("Closing Balance")
            ? `Closing Balance: ${numberWithCommas(
                parseFloat(unitHolder?.closing_balance || 0).toFixed(4),
              )}`
            : "",
        });
      });

      return { rows, nav, balance, openingBalanceIs };
    }

    // ── REGULAR UNITHOLDER path ───────────────────────────────────────────────

    // Build nav / balance strings (used as footer rows)
    unitHolderStatement?.slice(0, itemsToShow).forEach((unitHolder: any) => {
      const matchedNav = dailyNav?.find(
        (item) => item.fund === unitHolder.fund_code,
      );
      if (matchedNav?.nav > 0 && selectedValues?.columns?.includes("NAV")) {
        nav = `NAV: ${numberWithCommas((matchedNav.nav || 0).toFixed(4))}`;
      }
      if (matchedNav?.nav > 0) {
        balance = `Closing Balance: ${numberWithCommas(
          (+unitHolder.closingUnits * (matchedNav.nav || 0)).toFixed(4),
        )}`;
      }
    });

    // Determine active columns in fixed order
    const reorderedColumns = fixedOrder.filter((col) =>
      selectedValues?.columns?.includes(col),
    );

    if (reorderedColumns.length === 0) {
      return { rows: [], nav, balance, openingBalanceIs };
    }

    const lastCol = reorderedColumns[reorderedColumns.length - 1];

    const rows: any[] = [];

    unitHolderStatement?.slice(0, itemsToShow)?.forEach((ele) => {
      openingBalanceIs = ele?.openingUnits;

      const fundHeading = ele?.fund_name
        ? `${ele?.fund_name} (${ele.fund_code})`
        : ele?.fund_code;

      // ── Fund heading row ──────────────────────────────────────────────────
      // Put the fund name in the FIRST active column; opening balance in LAST.
      const headingRow: any = {};
      reorderedColumns.forEach((col, idx) => {
        if (idx === 0) {
          headingRow[col] = fundHeading;
        } else if (col === lastCol) {
          headingRow[col] = selectedValues?.balance?.includes("Opening Balance")
            ? `Opening Units: ${numberWithCommas(
                parseFloat(ele.openingUnits || 0).toFixed(4),
              )}`
            : "";
        } else {
          headingRow[col] = "";
        }
      });
      rows.push(headingRow);

      // ── Transaction rows ──────────────────────────────────────────────────
      if (ele?.forPeriodTxns?.length > 0) {
        ele.forPeriodTxns.forEach((ele1: any) => {
          const description =
            CapTxnType(ele1?.txnr_type) !== "Transfer of Units"
              ? `${CapTxnType(ele1?.txnr_type)}\n${ele1?.txn_id}`
              : ele1?.broker === "receiver"
                ? `${CapTxnType(ele1?.txnr_type)} (receiver)`
                : `${CapTxnType(ele1?.txnr_type)} (sender)`;

          // ✅ FIX: build the full row object keyed by column display name
          const txnRow: any = {
            Date: moment(ele1?.settlement_date).format("DD-MM-YYYY"),
            "TXN ID": ele1?.txn_id || "",
            Description: description,
            "Fund Units": numberWithCommas(
              parseFloat(ele1?.units || 0).toFixed(4),
            ),
            NAV: parseFloat(ele1?.nav || 0).toFixed(4),
            "Gross Amount": numberWithCommas(
              parseFloat(ele1?.gross_amount || 0).toFixed(4),
            ),
            Load: numberWithCommas(parseFloat(ele1?.load || 0).toFixed(4)),
            Tax: numberWithCommas(parseFloat(ele1?.tax || 0).toFixed(4)),
            "Net Amount": numberWithCommas(
              parseFloat(ele1?.net_amount || 0).toFixed(4),
            ),
            "Closing Units": numberWithCommas(
              parseFloat(ele1?.closingUnits || 0).toFixed(4),
            ),
          };

          // Only emit the selected columns
          const filteredRow: any = {};
          reorderedColumns.forEach((col) => {
            filteredRow[col] = txnRow[col] ?? "";
          });
          rows.push(filteredRow);
        });

        // ── Total row ─────────────────────────────────────────────────────
        const totalRow: any = {};
        reorderedColumns.forEach((col) => {
          if (col === "Date") {
            totalRow[col] = "Total";
          } else if (col === "Fund Units") {
            totalRow[col] = numberWithCommas(
              parseFloat(ele.total_units || 0).toFixed(4),
            );
          } else if (col === "Gross Amount") {
            totalRow[col] = numberWithCommas(
              parseFloat(ele.total_grossAmount || 0).toFixed(4),
            );
          } else if (col === "Load") {
            totalRow[col] = numberWithCommas(
              parseFloat(ele.total_load || 0).toFixed(4),
            );
          } else if (col === "Tax") {
            totalRow[col] = numberWithCommas(
              parseFloat(ele.total_tax || 0).toFixed(4),
            );
          } else if (col === "Net Amount") {
            totalRow[col] = numberWithCommas(
              parseFloat(ele.total_netAmount || 0).toFixed(4),
            );
          } else {
            totalRow[col] = "";
          }
        });
        rows.push(totalRow);

        // ── Closing balance row ───────────────────────────────────────────
        const closingRow: any = {};
        reorderedColumns.forEach((col) => {
          if (col === lastCol) {
            closingRow[col] = selectedValues?.balance?.includes(
              "Closing Balance",
            )
              ? `Closing Units: ${numberWithCommas(
                  parseFloat(ele.closingUnits || 0).toFixed(4),
                )}`
              : "";
          } else {
            closingRow[col] = "";
          }
        });
        rows.push(closingRow);
      } else {
        // No transactions — show placeholder
        const emptyRow: any = {};
        reorderedColumns.forEach((col, idx) => {
          emptyRow[col] = idx === 0 ? "No Recent Transactions!" : "";
        });
        rows.push(emptyRow);
      }
    });

    // ── Footer: NAV and closing balance value rows ────────────────────────────
    if (rows.length > 0) {
      const navRow: any = {};
      reorderedColumns.forEach((col) => {
        navRow[col] = col === lastCol ? nav : "";
      });
      rows.push(navRow);

      const balRow: any = {};
      reorderedColumns.forEach((col) => {
        balRow[col] = col === lastCol ? balance : "";
      });
      rows.push(balRow);
    }

    return { rows, nav, balance, openingBalanceIs };
  };

  // ── PDF — html2pdf path (Print button) ───────────────────────────────────────
  const handleGeneratePdf = () => {
    const element = contentRef.current;
    if (!element) return;

    const styleTag = document.createElement("style");
    styleTag.id = "pdf-flatten-style";
    styleTag.innerHTML = `
    #extra-sm {
      font-size: inherit !important;
      vertical-align: baseline !important;
      line-height: inherit !important;
      position: static !important;
    }
    table {
      page-break-inside: auto !important;
      border-collapse: collapse !important;
    }
    thead {
      display: table-header-group !important;
    }
    tfoot {
      display: table-footer-group !important;
    }
    tr {
      page-break-inside: avoid !important;
    }
    td, th {
      page-break-inside: auto !important;
    }
    p, div, span, h1, h2, h3, h4, h5, h6 {
      page-break-inside: auto !important;
    }
    .page-break-avoid {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
  `;
    document.head.appendChild(styleTag);

    const options = {
      filename: `Unit-Holder-Statement-${moment().format("YYYYMMDD")}.pdf`,
      margin: [10, 3, 10, 3],
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        windowHeight: (element as HTMLElement).scrollHeight,
        letterRendering: true,
      },
      jsPDF: {
        unit: "mm",
        format: paperSizeVal || "A4",
        orientation: layoutVal === "Landscape" ? "landscape" : "portrait",
      },
      pagebreak: {
        mode: ["css", "legacy"],
        before: ".page-break-before",
        after: ".page-break-after",
        avoid: [".page-break-avoid"],
      },
    };

    html2pdf()
      .from(element)
      .set(options)
      .toPdf()
      .get("pdf")
      .then((pdf: any) => {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(128, 128, 128);
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const pageMargin = 10; // mm
          const textX = pageMargin;
          pdf.text(
            "This is a computer-generated report from MACS 360. No signature is required.",
            textX,
            pageHeight - 5,
            { align: "left" },
          );
        }
      })
      .save()
      .then(() => {
        const injected = document.getElementById("pdf-flatten-style");
        if (injected) injected.remove();
      });
  };

  // ── Excel ─────────────────────────────────────────────────────────────────────
  const handleDownloadSampleFile = () => {
    const { rows } = buildExportRows();

    if (isParticipant) {
      const participantColumns = [
        "Date",
        "TXN ID",
        "Description",
        "Fund Units",
        "NAV",
        "Gross Amount",
        "Load",
        "Tax",
        "Net Amount",
        "Closing Units",
      ];
      generateExcel(
        "Unit Holder Statement Report",
        "Unit Holder Statement Report",
        "File",
        "File",
        "DCCL",
        [["Participant Unit Holder Statement"]],
        participantColumns,
        rows,
        [],
        [],
      );
      return;
    }

    const headings = [
      ["Unitholder"],
      [
        "Name",
        "ID",
        "From Date",
        "Report Date",
        "To Date",
        "Account Title",
        "Account No",
        "Tax Status",
        "Reinvest",
        "Zakat Exempt",
      ],
      [
        `${unitHolderData?.name || ""}`,
        `${resFolioNo}`,
        `${moment(resFromDate).format("DD-MM-YYYY")}`,
        `${moment(date).format("DD-MM-YYYY")}`,
        `${moment(resToDate).format("DD-MM-YYYY")}`,
        `${unitHolderData?.account_title || ""}`,
        `${unitHolderData?.account_no || ""}`,
        `${unitHolderData?.tax_status || ""}`,
        `${unitHolderData?.reinvest === true ? "True" : "False"}`,
        `${unitHolderData?.zakat_exempt === true ? "True" : "False"}`,
      ],
      [""],
    ];

    const reorderedColumns = fixedOrder.filter((col) =>
      selectedValues?.columns?.includes(col),
    );

    generateExcel(
      "Unit Holder Statement Report",
      "Unit Holder Statement Report",
      "File",
      "File",
      "DCCL",
      headings,
      reorderedColumns,
      rows,
      [],
      [],
    );
  };

  // ── TXT ───────────────────────────────────────────────────────────────────────
  const handleTXT = () => {
    const { rows } = buildExportRows();

    if (isParticipant) {
      exportToTxt(
        `"Participant Unit Holder Statement"\n`,
        selectedValues,
        rows,
        "Unit Holder Statement Report",
      );
      return;
    }

    let formattedContent = `"Unitholder"`;
    formattedContent += `"Name","ID","Account Title","Account No","Tax Status","Reinvest","Zakat Exempt"`;
    formattedContent += `"${unitHolderData?.name || ""}","${resFolioNo}","${unitHolderData?.account_title || ""}","${unitHolderData?.account_no || ""}","${unitHolderData?.tax_status || ""}","${unitHolderData?.reinvest === true ? "True" : "False"}","${unitHolderData?.zakat_exempt === true ? "True" : "False"}",,\n`;
    exportToTxt(
      formattedContent,
      selectedValues,
      rows,
      "Unit Holder Statement Report",
    );
  };

  const fundName = (value: string) =>
    allFunds?.find((ele) => ele?.symbol_code === value)?.fund_name ?? "";

  const previewWidth =
    dimensionOfPages?.find((item) => item?.key === paperSizeVal)?.dimensions[
      layoutVal === "Portrait" ? 0 : 1
    ] ?? "100%";

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Container fluid>
            <ToastContainer limit={1} />
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Unit Holder Statement</h4>
              </div>
              <div className="card-body">
                <UnitHolderSearchForm
                  fund={fund}
                  folioNo={folioNo}
                  fromDate={fromDate}
                  toDate={toDate}
                  allFunds={allFunds}
                  unitholderOptions={unitholderOptions}
                  folioLoading={folioLoading}
                  unitholderLoading={unitholderLoading}
                  loading={loading}
                  onFundChange={(value) => {
                    setFund(value);
                    setUnitHolderStatement([]);
                  }}
                  onFolioChange={(selectedOption) => {
                    setFolioNo(selectedOption ? selectedOption.value : "");
                    setUnitHolderData(selectedOption);
                    setUnitHolderStatement([]);
                  }}
                  onFromDateChange={(value) => {
                    setFromDateErr("");
                    setFromDate(value);
                    setUnitHolderStatement([]);
                  }}
                  onToDateChange={(value) => {
                    setToDateErr("");
                    setToDate(value);
                    setUnitHolderStatement([]);
                  }}
                  onSearch={() => {
                    if (isParticipant) searchDataParticipant();
                    else searchData();
                  }}
                  onUnitholderSearchInput={(value) =>
                    setUnitholderSearchInput(value)
                  }
                  folioNoErr={folioNoErr}
                  fromDateErr={fromDateErr}
                  toDateErr={toDateErr}
                  customStyles={customStyles}
                  theme={theme}
                  isParticipant={isParticipant}
                />
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                {unitHolderStatement?.length > 0 && (
                  <Row>
                    <ReportTemplates
                      ColumnArr={COLUMN_HEADINGS}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      pdfExportComponent={pdfExportComponent}
                      setFontSizeVal={setFontSizeVal}
                      fontSizeVal={fontSizeVal}
                      setPaperSizeVal={setPaperSizeVal}
                      paperSizeVal={paperSizeVal}
                      setLayoutVal={setLayoutVal}
                      layoutVal={layoutVal}
                      setScalePDF={setScalePDF}
                      scalePDF={scalePDF}
                      setMarginPDF={setMarginPDF}
                      marginPDF={marginPDF}
                      contentRef={contentRef}
                      fileName={`Unit-Holder-Statement-${moment().format("YYYYMMDD")}.pdf`}
                      setScreenSizePerPdf={setScreenSizePerPdf}
                      screenSizePerPdf={screenSizePerPdf}
                      setDimensionOfPages={setDimensionOfPages}
                      printpdf={handleGeneratePdf}
                      printExcel={handleDownloadSampleFile}
                      printTXT={handleTXT}
                    />
                  </Row>
                )}

                <div
                  id="printDiv"
                  className="p-4"
                  style={{
                    background: "white",
                    color: "black",
                    minWidth: "200px",
                    overflowX: "auto",
                    width: "auto",
                  }}
                >
                  <div
                    style={{
                      width: previewWidth,
                      height: "auto",
                      margin: "0 auto",
                      backgroundColor: "white",
                    }}
                    className="border"
                  >
                    <PDFExport
                      paperSize={paperSizeVal || "A4"}
                      margin={{
                        top: "1cm",
                        left: "1.5cm",
                        right: "1.5cm",
                        bottom: "1cm",
                      }}
                      scale={scalePDF}
                      landscape={layoutVal === "Landscape"}
                      repeatHeaders={true}
                      fileName={`Unit-Holder-Statement-${moment().format("YYYYMMDD")}.pdf`}
                      ref={pdfExportComponent}
                    >
                      <div
                        style={{ margin: "0.1in", whiteSpace: "nowrap" }}
                        ref={contentRef}
                      >
                        {unitHolderStatement.length > 0 ? (
                          <>
                            <ReportHeader
                              title="Statement of Account"
                              type="UH"
                              DatePattern={`${moment(resFromDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")} — ${moment(resToDate).format("DD-MMM-YYYY")?.replaceAll("-", " ")}`}
                              reportDate={moment(resToDate).format(
                                "DD-MMM-YYYY",
                              )}
                            />
                            <DividendCard
                              unitholderDetails={unitHolderData}
                              reportName={"UHR"}
                              report={true}
                              fromDate={moment(resFromDate).format(
                                "DD-MM-YYYY",
                              )}
                              resToDate={moment(resToDate).format("DD-MM-YYYY")}
                              toDate={moment().format("DD-MM-YYYY")}
                            />

                            <div
                              style={{ clear: "both", marginBottom: "4px" }}
                            />

                            {selectedValues?.columns?.length > 0 ? (
                              <div>
                                {isParticipant ? (
                                  <ParticipantStatementTable
                                    participantRows={participantRows}
                                    fundName={fundName}
                                    CapTxnType={CapTxnType}
                                  />
                                ) : (
                                  <RegularStatementTable
                                    unitHolderStatement={unitHolderStatement}
                                    selectedValues={selectedValues}
                                    CapTxnType={CapTxnType}
                                    dailyNav={dailyNav}
                                    itemsToShow={itemsToShow}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="text-center my-5">
                                <b>No column selected</b>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="table-empty">
                            <BsTable className="icon" />
                            <p className="text-center empty-text text-primary">
                              No record found
                            </p>
                          </div>
                        )}
                      </div>
                      {unitHolderStatement.length > 0 && (
                        <div className="d-flex justify-content-end">
                          <small className="p-2">
                            This is a computer-generated report from MACS 360.
                            No signature is required.
                          </small>
                        </div>
                      )}
                    </PDFExport>
                  </div>
                </div>

                {itemsToShow < unitHolderStatement?.length &&
                  selectedValues?.columns?.length > 0 && (
                    <div className="d-flex justify-content-center my-2">
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() =>
                          setItemsToShow((prev) => prev + itemsPerPage)
                        }
                      >
                        Show More
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </div>
  );
};

export default UnitHolderStatement;
